/**
 * Contexto de autenticación: login (Google Workspace o correo/contraseña),
 * aprovisionamiento del usuario, roles (custom claims) y perfil (accesos).
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { api } from '../servicios/api';
import { COLECCIONES, esAdminModulo, type Rol, type Usuario } from '@cji/shared';

interface AuthState {
  usuario: User | null;
  perfil: Usuario | null;
  roles: Rol[];
  esAdmin: boolean;
  cargando: boolean;
  iniciarSesion: () => Promise<void>;
  iniciarConCorreo: (email: string, password: string) => Promise<void>;
  cerrarSesion: () => Promise<void>;
  refrescar: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<User | null>(null);
  const [perfil, setPerfil] = useState<Usuario | null>(null);
  const [roles, setRoles] = useState<Rol[]>([]);
  const [esAdmin, setEsAdmin] = useState(false);
  const [cargando, setCargando] = useState(true);

  async function cargarDatos(u: User) {
    const token = await u.getIdTokenResult(true);
    const r = token.claims.roles;
    setRoles(Array.isArray(r) ? (r as Rol[]) : []);
    setEsAdmin(esAdminModulo(u.email, token.claims.admin));
    try {
      // trz_usuarios está indexado por CORREO (identidad alineada al portal).
      const snap = u.email ? await getDoc(doc(db, COLECCIONES.usuarios, u.email)) : null;
      setPerfil(snap && snap.exists() ? ({ uid: u.uid, ...(snap.data() as object) } as Usuario) : null);
    } catch {
      setPerfil(null);
    }
  }

  useEffect(() => {
    // SSO desde el portal: si viene #ct=<customToken> en la URL, iniciar
    // sesión automáticamente (sin pedir login) con el mismo usuario del portal.
    let esperandoSSO = false;
    const m = window.location.hash.match(/[#&]ct=([^&]+)/);
    if (m) {
      esperandoSSO = true;
      const ct = decodeURIComponent(m[1]);
      // Limpia el token de la URL para que no quede en el historial.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      signInWithCustomToken(auth, ct).catch(() => {
        esperandoSSO = false;
        setCargando(false); // token vencido/ inválido → login normal
      });
    }

    return onAuthStateChanged(auth, async (u) => {
      // Mientras el custom token se canjea, no mostrar la pantalla de login.
      if (!u && esperandoSSO) return;
      esperandoSSO = false;
      setUsuario(u);
      if (u) {
        try {
          await api.provisionarUsuario({});
        } catch {
          /* ya existe o dominio no autorizado */
        }
        await cargarDatos(u);
      } else {
        setRoles([]);
        setPerfil(null);
        setEsAdmin(false);
      }
      setCargando(false);
    });
  }, []);

  const valor: AuthState = {
    usuario,
    perfil,
    roles,
    esAdmin,
    cargando,
    iniciarSesion: async () => {
      await signInWithPopup(auth, googleProvider);
    },
    iniciarConCorreo: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    cerrarSesion: async () => {
      await fbSignOut(auth);
    },
    refrescar: async () => {
      if (auth.currentUser) await cargarDatos(auth.currentUser);
    },
  };

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return c;
}
